import { z } from "zod";
import { newPasswordSchema } from "./account-credentials.js";
import { dealerPermissionDefinitions, staffPermissionDefinitions } from "./permissions.js";
import { productSearchFiltersSchema } from "./product-search.js";
import { categoryPrefixSchema, matchesCategoryPrefix } from "./categories.js";
export * from "./promotions.js";
export * from "./categories.js";
export * from "./permissions.js";
export * from "./account-credentials.js";
export * from "./customer-editor.js";
export * from "./product-search.js";
export * from "./order-print-settings.js";
export const accountTypeSchema = z.enum(["DEALER", "STAFF"]);
export const staffRoleSchema = z.enum(["ADMIN", "VIEWER", "CUSTOM"]);
export const staffPermissionSchema = z.enum(staffPermissionDefinitions.map(({ id }) => id));
export const dealerPermissionSchema = z.enum(dealerPermissionDefinitions.map(({ id }) => id));
export const orderStatusSchema = z.enum(["ACTIVE", "CANCELLED"]);
export const fulfillmentStatusSchema = z.enum([
    "CONFIRMED",
    "PICKING",
    "DISPATCHED",
    "COMPLETED",
]);
export const paymentStatusSchema = z.enum(["UNPAID", "PARTIAL", "PAID"]);
export const paymentEntryTypeSchema = z.enum(["PAYMENT", "REVERSAL"]);
export const inventoryMovementTypeSchema = z.enum([
    "PURCHASE_IN",
    "SALES_OUT",
    "ADJUSTMENT_IN",
    "ADJUSTMENT_OUT",
]);
const requiredText = (label, max = 200) => z.string().trim().min(1, `${label}不能为空`).max(max, `${label}过长`);
export const loginSchema = z.object({
    username: requiredText("用户名", 80),
    password: z.string().min(1, "请输入密码").max(200),
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1),
    newPassword: newPasswordSchema,
});
export const addressInputSchema = z.object({
    label: requiredText("地址名称", 50).default("收货地址"),
    contactName: requiredText("联系人", 80),
    phone: requiredText("联系电话", 30),
    province: z.string().trim().max(50, "省份过长").default(""),
    city: z.string().trim().max(50, "城市过长").default(""),
    district: z.string().trim().max(50).default(""),
    detail: requiredText("详细地址", 300),
    isDefault: z.boolean().default(false),
});
// All address forms accept one complete delivery line without guessing its region.
// A customer's optional initial address defaults to its main delivery address.
export const customerDefaultAddressSchema = addressInputSchema.extend({
    isDefault: z.boolean().default(true),
});
export const catalogQuerySchema = productSearchFiltersSchema.extend({
    categoryScope: z.enum(['direct']).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(60).default(30),
});
export const cartDraftInputSchema = z
    .object({
    revision: z.number().int().positive(),
    selectedAddressId: z.string().uuid().nullable().default(null),
    note: z.string().trim().max(500).default(""),
    items: z
        .array(z.object({
        skuId: z.string().uuid(),
        quantity: z.number().int().positive().max(999_999),
    }))
        .max(200),
})
    .superRefine((value, context) => {
    const seen = new Set();
    for (const [index, item] of value.items.entries()) {
        if (seen.has(item.skuId)) {
            context.addIssue({
                code: "custom",
                message: "同一 SKU 不能在草稿中重复出现",
                path: ["items", index, "skuId"],
            });
        }
        seen.add(item.skuId);
    }
});
export const createOrderSchema = z
    .object({
    deliveryMethod: z.enum(["SELF_PICKUP", "DELIVERY"]).default("DELIVERY"),
    addressId: z.string().uuid().optional(),
    note: z.string().trim().max(500).default(""),
    idempotencyKey: z.string().uuid(),
    items: z
        .array(z.object({
        skuId: z.string().uuid(),
        quantity: z.number().int().positive().max(999_999),
        expectedUnitPriceCents: z.number().int().nonnegative(),
        unitPriceCents: z.never().optional(),
    }))
        .min(1, "请至少选择一个商品")
        .max(200),
})
    .superRefine((value, context) => {
    if (value.deliveryMethod === "DELIVERY" && !value.addressId) {
        context.addIssue({ code: "custom", message: "送货上门请选择收货地址", path: ["addressId"] });
    }
    const seen = new Set();
    for (const [index, item] of value.items.entries()) {
        if (seen.has(item.skuId)) {
            context.addIssue({
                code: "custom",
                message: "同一 SKU 不能在订单中重复出现",
                path: ["items", index, "skuId"],
            });
        }
        seen.add(item.skuId);
    }
});
export const adminOrderCustomerSchema = z.object({
    name: requiredText("客户名称", 200),
    contactName: z.string().trim().max(80).default(""),
    phone: z.string().trim().max(30).default(""),
});
export const adminOrderAddressSchema = z.object({
    label: z.string().trim().max(50).default("送货地址"),
    contactName: z.string().trim().max(80).default(""),
    phone: z.string().trim().max(30).default(""),
    province: z.string().trim().max(50).default(""),
    city: z.string().trim().max(50).default(""),
    district: z.string().trim().max(50).default(""),
    detail: requiredText("详细地址", 300),
});
export const createAdminOrderSchema = z.object({
    note: createOrderSchema.shape.note,
    idempotencyKey: createOrderSchema.shape.idempotencyKey,
    items: z.array(createOrderSchema.shape.items.element.extend({
        unitPriceCents: z.number().int().nonnegative().max(999_999_999).optional(),
    })).min(1, "请至少选择一个商品").max(200),
    customerId: z.string().uuid().optional(),
    customerInfo: adminOrderCustomerSchema,
    deliveryMethod: z.enum(["SELF_PICKUP", "DELIVERY"]).default("SELF_PICKUP"),
    addressId: z.string().uuid().optional(),
    address: adminOrderAddressSchema.optional(),
}).superRefine((value, context) => {
    const order = createOrderSchema.safeParse({ ...value,
        items: value.items.map((item) => ({ skuId: item.skuId, quantity: item.quantity, expectedUnitPriceCents: item.expectedUnitPriceCents })),
        addressId: value.addressId ?? "00000000-0000-4000-8000-000000000001" });
    if (!order.success) {
        for (const issue of order.error.issues)
            context.addIssue({ code: "custom", message: issue.message, path: issue.path });
    }
    if (value.addressId && !value.customerId) {
        context.addIssue({ code: "custom", message: "选择客户后才能使用客户收货地址", path: ["addressId"] });
    }
    if (value.address && value.addressId) {
        context.addIssue({ code: "custom", message: "收货地址只能选择或填写一种", path: ["address"] });
    }
    if (value.deliveryMethod === "DELIVERY" && !value.address && !value.addressId) {
        context.addIssue({ code: "custom", message: "送货订单必须填写或选择收货地址", path: ["address"] });
    }
    if (value.deliveryMethod === "DELIVERY" && value.address) {
        if (!(value.address.contactName || value.customerInfo.contactName || value.customerInfo.name)) {
            context.addIssue({ code: "custom", message: "送货订单必须填写联系人", path: ["address", "contactName"] });
        }
        if (!(value.address.phone || value.customerInfo.phone)) {
            context.addIssue({ code: "custom", message: "送货订单必须填写联系电话", path: ["address", "phone"] });
        }
    }
});
export const customerInputSchema = z.object({
    code: requiredText("客户编码", 50).regex(/^[A-Za-z0-9_-]+$/, "客户编码只能包含字母、数字、横线和下划线"),
    name: requiredText("客户名称", 160),
    username: requiredText("登录名", 80).regex(/^[A-Za-z0-9_.-]+$/, "登录名格式不正确"),
    contactName: requiredText("联系人", 80),
    phone: requiredText("联系电话", 30),
    active: z.boolean().default(true),
    dealerPermissions: z.array(dealerPermissionSchema).max(dealerPermissionDefinitions.length).optional(),
    defaultAddress: customerDefaultAddressSchema.optional(),
});
export const customerUpdateSchema = z
    .object({
    name: requiredText("客户名称", 160).optional(),
    contactName: requiredText("联系人", 80).optional(),
    phone: requiredText("联系电话", 30).optional(),
    active: z.boolean().optional(),
    dealerPermissions: z.array(dealerPermissionSchema).max(dealerPermissionDefinitions.length).optional(),
})
    .refine((value) => Object.keys(value).length > 0, "没有需要更新的内容");
export const staffInputSchema = z.object({
    username: requiredText("登录名", 80).regex(/^[A-Za-z0-9_.-]+$/, "登录名格式不正确"),
    displayName: requiredText("姓名", 80),
    staffRole: staffRoleSchema.default("VIEWER"),
    staffPermissions: z.array(staffPermissionSchema).max(staffPermissionDefinitions.length).optional(),
    active: z.boolean().default(true),
});
export const staffUpdateSchema = z
    .object({
    displayName: requiredText("姓名", 80).optional(),
    staffRole: staffRoleSchema.optional(),
    staffPermissions: z.array(staffPermissionSchema).max(staffPermissionDefinitions.length).optional(),
    active: z.boolean().optional(),
})
    .refine((value) => Object.keys(value).length > 0, "没有需要更新的内容");
export const categoryInputSchema = z.object({
    name: requiredText("分类名称", 80),
    sortOrder: z.number().int().min(0).max(99_999).default(0),
    active: z.boolean().default(true),
});
export const productInputSchema = z.object({
    categoryId: z.string().uuid(),
    code: requiredText("商品编码", 50).regex(/^[A-Za-z0-9_-]+$/, "商品编码格式不正确"),
    name: requiredText("商品名称", 160),
    description: z.string().trim().max(1000).default(""),
    active: z.boolean().default(true),
});
export const productUpdateSchema = productInputSchema
    .extend({ description: z.string().trim().max(1000), active: z.boolean() })
    .partial()
    .refine((value) => Object.keys(value).length > 0, "没有需要更新的内容");
export const skuInputSchema = z.object({
    productId: z.string().uuid(),
    code: requiredText("SKU 编码", 60).regex(/^[A-Za-z0-9_.-]+$/, "SKU 编码格式不正确"),
    specification: requiredText("规格", 180),
    unit: requiredText("销售单位", 20),
    priceCents: z.number().int().nonnegative().max(999_999_999),
    active: z.boolean().default(true),
    sortOrder: z.number().int().min(0).max(99_999).default(0),
    newProduct: productInputSchema.optional(),
    productName: productInputSchema.shape.name.optional(),
});
export const skuUpdateSchema = skuInputSchema
    .omit({ productId: true })
    .extend({ syncProductCode: z.boolean().optional() })
    .partial()
    .refine((value) => Object.keys(value).length > 0, "没有需要更新的内容");
export const productCreateSchema = productInputSchema.extend({
    categoryId: productInputSchema.shape.categoryId.optional(),
    categoryPrefix: categoryPrefixSchema.optional(),
    initialSku: skuInputSchema
        .pick({ specification: true, unit: true, priceCents: true })
        .partial({ specification: true })
        .optional(),
}).refine((input) => Boolean(input.categoryId) !== Boolean(input.categoryPrefix), "请选择一个商品分类")
    .refine((input) => !input.categoryPrefix || matchesCategoryPrefix(input.code, input.categoryPrefix), {
    message: "商品编码须以所选分类编码开头，并补齐商品序号", path: ["code"],
});
export const paymentInputSchema = z.object({
    idempotencyKey: z.string().uuid(),
    amountCents: z.number().int().positive().max(999_999_999),
    paymentDate: z.iso.date(),
    method: requiredText("收款方式", 40),
    referenceNo: z.string().trim().max(100).default(""),
    note: z.string().trim().max(500).default(""),
});
export const reversePaymentSchema = z.object({
    reason: requiredText("冲销原因", 300),
});
export const cancelOrderSchema = z.object({
    reason: requiredText("取消原因", 300),
});
export const fulfillmentStatusUpdateSchema = z.object({
    status: fulfillmentStatusSchema,
});
export const inventoryStockQuerySchema = catalogQuerySchema;
export const inventoryMovementQuerySchema = inventoryStockQuerySchema.extend({
    movementType: inventoryMovementTypeSchema.optional(),
    dateFrom: z.iso.date().optional(),
    dateTo: z.iso.date().optional(),
});
export const inventorySkuSearchQuerySchema = productSearchFiltersSchema.extend({
    limit: z.coerce.number().int().min(1).max(60).default(30),
});
export const inventoryMovementInputSchema = z
    .object({
    idempotencyKey: z.string().uuid(),
    movementType: inventoryMovementTypeSchema,
    occurredOn: z.iso.date(),
    counterparty: z.string().trim().max(160, "往来单位过长").default(""),
    referenceNo: z.string().trim().max(100, "关联单号过长").default(""),
    note: z.string().trim().max(500, "备注过长").default(""),
    lines: z
        .array(z.object({
        skuId: z.string().uuid(),
        quantity: z.number().int().positive().max(99_999_999),
    }))
        .min(1, "请至少添加一个 SKU")
        .max(100),
})
    .superRefine((value, context) => {
    const seen = new Set();
    for (const [index, line] of value.lines.entries()) {
        if (seen.has(line.skuId)) {
            context.addIssue({
                code: "custom",
                message: "同一 SKU 不能重复添加",
                path: ["lines", index, "skuId"],
            });
        }
        seen.add(line.skuId);
    }
});
export const inventoryReversalSchema = z.object({
    reason: requiredText("冲销原因", 300),
});
export function formatCny(cents) {
    return new Intl.NumberFormat("zh-CN", {
        style: "currency",
        currency: "CNY",
        minimumFractionDigits: 2,
    }).format(cents / 100);
}
export function paymentStatus(totalCents, paidCents) {
    if (paidCents <= 0)
        return "UNPAID";
    if (paidCents >= totalCents)
        return "PAID";
    return "PARTIAL";
}
export function canCancelOrder(effectivePaidCents) {
    return effectivePaidCents === 0;
}
export function canAdvanceFulfillment(current, target) {
    if (current === "CONFIRMED" && target === "DISPATCHED")
        return true;
    const next = {
        CONFIRMED: "PICKING",
        PICKING: "DISPATCHED",
        DISPATCHED: "COMPLETED",
        COMPLETED: null,
    };
    return next[current] === target;
}
export function todayInShanghai(now = new Date()) {
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Shanghai",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(now);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return `${values.year}-${values.month}-${values.day}`;
}
export function parseYuanToCents(value) {
    const normalized = value.trim();
    if (!/^\d+(?:\.\d{1,2})?$/.test(normalized))
        return null;
    const [yuanPart, decimalPart = ""] = normalized.split(".");
    const cents = Number(yuanPart) * 100 + Number(decimalPart.padEnd(2, "0"));
    return Number.isSafeInteger(cents) ? cents : null;
}
